import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as MicIcon } from "../../../../assets/icons/svg/mic.svg";
import { ReactComponent as StopRecording } from "../../../../assets/icons/svg/stop-recording.svg";
import { ReactComponent as PlayRecording } from "../../../../assets/icons/svg/play-recording.svg";
import { ReactComponent as PauseRecording } from "../../../../assets/icons/svg/puse-recording.svg";
import { ReactComponent as DeleteRecording } from "../../../../assets/icons/svg/Delete-recording.svg";
import { ReactComponent as ResetRecordingActive } from "../../../../assets/icons/svg/reset-recording-active.svg";
import { ReactComponent as ResetRecordingInactive } from "../../../../assets/icons/svg/reset-recording-inactive.svg";

// Import WaveSurfer and RecordPlugin
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record";
import { DEFAULT_FONT_FAMILY } from "../../../../config/fontConfig";

type RecorderState = "idle" | "recording" | "recorded" | "playing";

/**
 * Audio conversion utility for MP3 format
 */
class AudioProcessor {
  /**
   * Convert audio blob to MP3 format using lamejs library
   */
  static async convertToMP3(audioBlob: Blob): Promise<Blob> {
    try {
      console.log("Starting MP3 conversion...", {
        originalSize: audioBlob.size,
        originalType: audioBlob.type,
      });

      // Create an audio context
      const audioContext = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();

      // Convert blob to array buffer
      const arrayBuffer = await audioBlob.arrayBuffer();

      // Decode audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      console.log("Audio decoded:", {
        duration: audioBuffer.duration,
        sampleRate: audioBuffer.sampleRate,
        channels: audioBuffer.numberOfChannels,
      });

      // Get audio samples
      const samples = audioBuffer.getChannelData(0); // Get first channel
      const sampleRate = audioBuffer.sampleRate;

      // Convert Float32Array to Int16Array for MP3 encoding
      const int16Samples = new Int16Array(samples.length);
      for (let i = 0; i < samples.length; i++) {
        const s = Math.max(-1, Math.min(1, samples[i]));
        int16Samples[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }

      // Import lamejs dynamically
      const lamejs = await import("lamejs");

      // Create MP3 encoder
      const mp3encoder = new lamejs.Mp3Encoder(1, sampleRate, 128); // mono, sample rate, bitrate

      // Encode to MP3
      const mp3Data: number[] = [];
      const sampleBlockSize = 1152; // Standard MP3 frame size

      for (let i = 0; i < int16Samples.length; i += sampleBlockSize) {
        const sampleChunk = int16Samples.subarray(i, i + sampleBlockSize);
        const mp3buf = mp3encoder.encodeBuffer(sampleChunk);
        if (mp3buf.length > 0) {
          mp3Data.push(...Array.from(mp3buf));
        }
      }

      // Flush remaining data
      const mp3buf = mp3encoder.flush();
      if (mp3buf.length > 0) {
        mp3Data.push(...Array.from(mp3buf));
      }

      // Create Uint8Array from the collected data
      const mp3Buffer = new Uint8Array(mp3Data);

      // Combine all MP3 data
      const mp3Blob = new Blob([mp3Buffer], { type: "audio/mp3" });

      console.log("MP3 conversion successful:", {
        originalSize: audioBlob.size,
        mp3Size: mp3Blob.size,
        compression:
          ((1 - mp3Blob.size / audioBlob.size) * 100).toFixed(2) + "%",
      });

      return mp3Blob;
    } catch (error) {
      console.error("MP3 conversion failed:", error);
      // Fallback: return blob with MP3 mime type
      console.warn("Using fallback: changing MIME type to audio/mp3");
      return new Blob([audioBlob], { type: "audio/mp3" });
    }
  }
}

interface VoiceNoteRecorderProps {
  onRecordingComplete?: (
    hasRecording: boolean,
    audioUrl?: string,
    downloadUrl?: string,
    filename?: string,
    audioBlob?: Blob,
  ) => void;
  initialState?: RecorderState;
  initialAudioUrl?: string;
  disabled?: boolean;
  key?: string;
  compact?: boolean;
}

const getMicrophoneStream = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true, // Suppresses background noise
        autoGainControl: true,
        sampleRate: 48000,
      },
    });
    return stream;
  } catch (error) {
    console.error("Error accessing microphone:", error);
    throw error;
  }
};

const VoiceNoteRecorder: React.FC<VoiceNoteRecorderProps> = ({
  onRecordingComplete,
  initialState = "idle",
  initialAudioUrl,
  disabled = false,
  compact = false,
}) => {
  const [recorderState, setRecorderState] = useState<RecorderState>("idle");
  const [seconds, setSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [progress, setProgress] = useState(0);
  const [waveformBars, setWaveformBars] = useState(30);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const recordPluginRef = useRef<RecordPlugin | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const isResettingRef = useRef(false);
  const isInitialized = useRef(false);

  // Get responsive dimensions and classes
  const getResponsiveClasses = () => {
    const screenWidth = window.innerWidth;

    let containerHeight = "h-8";
    let iconSize = "w-5 h-5";
    let buttonSize = "w-8 h-8";
    let textSizeTitle = "text-sm";
    let textSizeTime = "text-xs";
    let waveformPadding = "pl-3 pr-2";
    let buttonGap = "gap-3";

    if (screenWidth >= 2560) {
      containerHeight = "h-14";
      iconSize = "w-8 h-8";
      buttonSize = "w-14 h-14";
      textSizeTitle = "text-xl";
      textSizeTime = "text-lg";
      waveformPadding = "pl-4 pr-3";
      buttonGap = "gap-4";
    } else if (screenWidth >= 1920) {
      containerHeight = "h-12";
      iconSize = "w-6 h-6";
      buttonSize = "w-12 h-12";
      textSizeTitle = "text-lg";
      textSizeTime = "text-base";
      waveformPadding = "pl-4 pr-3";
      buttonGap = "gap-4";
    } else if (screenWidth >= 1600) {
      containerHeight = "h-10";
      iconSize = "w-6 h-6";
      buttonSize = "w-10 h-10";
      textSizeTitle = "text-base";
      textSizeTime = "text-sm";
      waveformPadding = "pl-3 pr-2";
      buttonGap = "gap-3";
    } else if (screenWidth >= 1440) {
      containerHeight = "h-9";
      iconSize = "w-6 h-6";
      buttonSize = "w-9 h-9";
      textSizeTitle = "text-sm";
      textSizeTime = "text-xs";
      waveformPadding = "pl-3 pr-2";
      buttonGap = "gap-3";
    }

    return {
      containerHeight,
      iconSize,
      buttonSize,
      textSizeTitle,
      textSizeTime,
      waveformPadding,
      buttonGap,
    };
  };

  const responsiveClasses = getResponsiveClasses();

  const stopTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // In VoiceNoteRecorder.tsx - Update the initialization useEffect
  useEffect(() => {
    console.log("VoiceNoteRecorder - Component mounted/key changed:", {
      initialState,
      initialAudioUrl: initialAudioUrl
        ? initialAudioUrl.substring(0, 50) + "..."
        : "none",
      isInitialized: isInitialized.current,
      disabled,
    });

    // ✅ FIX: Don't reset state if we're loading existing audio
    const hasExistingAudio = initialAudioUrl && initialState === "recorded";

    if (!hasExistingAudio) {
      isInitialized.current = false;
      setRecorderState("idle");
      setSeconds(0);
      setProgress(0);
      setAudioUrl(null);
      setAudioBlob(null);
      setDownloadUrl(null);
      setFilename("");

      if (audioRef.current) {
        audioRef.current.src = "";
        audioRef.current.load();
      }
    }

    const initTimeout = setTimeout(() => {
      console.log("VoiceNoteRecorder - Setting initial state:", {
        initialState,
        initialAudioUrl: initialAudioUrl
          ? initialAudioUrl.substring(0, 50) + "..."
          : "none",
      });

      if (hasExistingAudio) {
        console.log("VoiceNoteRecorder - Loading initial audio");

        // ✅ CRITICAL FIX: Check if it's an API URL
        const isApiUrl = initialAudioUrl.startsWith("http");

        if (isApiUrl) {
          // ✅ API URL: Must fetch with auth and convert to blob URL
          console.log("🌐 API URL detected - fetching with authentication...");

          // Set state immediately to show "recorded" UI
          setRecorderState("recorded");
          isInitialized.current = true;

          // Fetch in background
          (async () => {
            try {
              // ✅ Import EnhancedTokenManager
              const { EnhancedTokenManager } =
                await import("../../../../utils/tokenManager");
              const accessToken = EnhancedTokenManager.getAccessToken();

              if (!accessToken) {
                throw new Error("No access token available");
              }

              console.log("🔐 Fetching audio with token...");

              const response = await fetch(initialAudioUrl, {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              });

              if (!response.ok) {
                throw new Error(
                  `HTTP ${response.status}: ${response.statusText}`,
                );
              }

              const blob = await response.blob();
              console.log("✅ Audio blob fetched:", {
                size: blob.size,
                type: blob.type,
              });

              // Create blob URL
              const blobUrl = URL.createObjectURL(blob);
              console.log("✅ Blob URL created:", blobUrl.substring(0, 50));

              // Update state with blob URL
              setAudioUrl(blobUrl);
              setAudioBlob(blob);
              setDownloadUrl(blobUrl);

              if (audioRef.current) {
                audioRef.current.src = blobUrl;
                audioRef.current.load();

                const handleCanPlay = () => {
                  console.log("✅ Audio loaded and ready for playback");
                };

                audioRef.current.addEventListener("canplay", handleCanPlay, {
                  once: true,
                });
              }
            } catch (error) {
              console.error("❌ Failed to fetch audio from API:", error);
              // Keep recorded state but show error
              console.warn("Audio unavailable but maintaining recorded state");
            }
          })();
        } else {
          // ✅ Blob URL: Use directly
          console.log("💾 Blob URL detected - using directly");
          setAudioUrl(initialAudioUrl);
          setRecorderState("recorded");
          isInitialized.current = true;

          if (audioRef.current) {
            audioRef.current.src = initialAudioUrl;
            audioRef.current.load();

            const handleCanPlay = () => {
              console.log("VoiceNoteRecorder - Initial audio loaded and ready");
            };

            const handleError = (e: Event) => {
              console.error(
                "VoiceNoteRecorder - Error loading initial audio:",
                e,
              );
              console.warn(
                "Audio failed to load but maintaining recorded state",
              );
            };

            audioRef.current.addEventListener("canplay", handleCanPlay, {
              once: true,
            });
            audioRef.current.addEventListener("error", handleError, {
              once: true,
            });
          }
        }
      } else {
        setRecorderState(initialState || "idle");
        isInitialized.current = true;
      }
    }, 50);

    return () => {
      clearTimeout(initTimeout);
    };
  }, [initialState, initialAudioUrl, disabled]);

  // Initialize WaveSurfer for recording
  useEffect(() => {
    if (disabled) {
      console.log("Skipping WaveSurfer init - disabled");
      return;
    }

    const initTimer = setTimeout(() => {
      if (!waveformRef.current) {
        console.log("Waveform container not ready yet");
        return;
      }

      console.log("Initializing WaveSurfer for recording");
      console.log("Waveform container:", waveformRef.current);

      if (wavesurferRef.current) {
        console.log("Destroying previous WaveSurfer instance");
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }

      try {
        const wavesurfer = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: "#9ca3af",
          progressColor: "#253057",
          height: 48,
          barWidth: 2,
          barGap: 4,
          barRadius: 2,
          normalize: false,
        });

        console.log("WaveSurfer instance created:", wavesurfer);
        wavesurferRef.current = wavesurfer;

        const record = wavesurfer.registerPlugin(
          RecordPlugin.create({
            renderRecordedAudio: false,
            scrollingWaveform: true,
            continuousWaveform: false,
            audioBitsPerSecond: 128000,
            mimeType: "audio/webm",
          }),
        );
        console.log("RecordPlugin registered:", record);
        recordPluginRef.current = record;

        record.on("record-end", (blob: Blob) => {
          console.log("Recording ended via WaveSurfer, blob size:", blob.size);
          handleRecordingComplete(blob);
        });

        record.on("record-progress", (time: number) => {
          const seconds = Math.floor(time / 1000);
          setSeconds(seconds);
        });

        console.log("WaveSurfer initialization complete");
      } catch (error) {
        console.error("Error initializing WaveSurfer:", error);
        console.error("Error details:", {
          name: (error as Error).name,
          message: (error as Error).message,
          stack: (error as Error).stack,
        });
      }
    }, 100);

    return () => {
      clearTimeout(initTimer);
      console.log("Cleaning up WaveSurfer");
      if (recordPluginRef.current) {
        recordPluginRef.current.destroy();
        recordPluginRef.current = null;
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
    };
  }, [disabled]);

  const startRecording = async () => {
    if (disabled) {
      console.warn("Recording disabled");
      return;
    }

    if (!recordPluginRef.current || !wavesurferRef.current) {
      console.error("RecordPlugin or WaveSurfer not initialized");
      alert("Recording plugin not ready. Please refresh the page.");
      return;
    }

    try {
      isResettingRef.current = false;

      console.log("Starting WaveSurfer recording...");
      console.log("RecordPlugin instance:", recordPluginRef.current);
      console.log("WaveSurfer instance:", wavesurferRef.current);

      // Request microphone with noise suppression constraints
      const stream = await getMicrophoneStream();

      // Start recording with the configured stream
      await recordPluginRef.current.startRecording({
        deviceId: stream.getAudioTracks()[0].getSettings().deviceId,
      });

      setRecorderState("recording");
      setSeconds(0);
      startTimeRef.current = Date.now();

      // Move waveform to display
      const moveWaveform = () => {
        const displayContainer = document.getElementById(
          "recording-waveform-display",
        );
        const hiddenContainer = waveformRef.current;

        console.log("Attempting to move waveform to display:", {
          displayContainer,
          hiddenContainer,
          hasChildren: hiddenContainer?.children.length,
        });

        if (displayContainer && hiddenContainer) {
          // Clear display container first
          while (displayContainer.firstChild) {
            displayContainer.removeChild(displayContainer.firstChild);
          }

          // Move all children from hidden to display
          while (hiddenContainer.firstChild) {
            displayContainer.appendChild(hiddenContainer.firstChild);
          }
          console.log(
            "Waveform moved successfully, display container children:",
            displayContainer.children.length,
          );
          return true;
        }
        return false;
      };

      // Try immediately
      setTimeout(() => {
        if (!moveWaveform()) {
          // If first attempt fails, try again after a longer delay
          setTimeout(moveWaveform, 100);
        }
      }, 50);

      console.log("WaveSurfer recording started successfully");
    } catch (error) {
      console.error("Error starting recording:", error);
      console.error("Error details:", {
        name: (error as Error).name,
        message: (error as Error).message,
        stack: (error as Error).stack,
      });
      alert(
        `Could not access microphone. Error: ${
          (error as Error).message
        }\n\nPlease check permissions.`,
      );
    }
  };

  const stopRecording = async () => {
    if (disabled || !recordPluginRef.current) return;

    console.log("VoiceNoteRecorder - Stopping WaveSurfer recording");

    try {
      // ✅ INSTANT STATE CHANGE - Change to recorded immediately
      setRecorderState("recorded");
      setIsProcessing(true); // Show processing indicator
      stopTimer();

      // Stop recording in background
      await recordPluginRef.current.stopRecording();
    } catch (error) {
      console.error("Error stopping recording:", error);
      setRecorderState("idle");
      setIsProcessing(false);
    }
  };

  const handleRecordingComplete = async (blob: Blob) => {
    if (isResettingRef.current) {
      console.log("VoiceNoteRecorder - Recording stopped due to reset");
      isResettingRef.current = false;
      setIsProcessing(false);
      return;
    }

    try {
      console.log("VoiceNoteRecorder - Processing recorded audio blob:", {
        size: blob.size,
        type: blob.type,
      });

      // Convert to MP3 format
      console.log("VoiceNoteRecorder - Converting audio to MP3...");
      const mp3Blob = await AudioProcessor.convertToMP3(blob);

      const url = URL.createObjectURL(mp3Blob);

      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);
      const fileName = `voice-note-${timestamp}.mp3`;

      console.log("VoiceNoteRecorder - Audio created:", {
        blobSize: mp3Blob.size,
        blobType: mp3Blob.type,
        url: url,
        filename: fileName,
      });

      setAudioBlob(mp3Blob);
      setAudioUrl(url);
      setDownloadUrl(url);
      setFilename(fileName);
      setProgress(0);
      // State is already "recorded" from stopRecording
      setIsProcessing(false); // ✅ Processing complete

      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.currentTime = 0;
        audioRef.current.load();

        setTimeout(() => {
          isInitialized.current = true;
          console.log("VoiceNoteRecorder - Audio ready for playback");
        }, 100); // Reduced from 500ms
      }

      console.log("VoiceNoteRecorder - Notifying parent with audio:", {
        hasRecording: true,
        audioUrl: url,
        downloadUrl: url,
        filename: fileName,
        blobSize: mp3Blob.size,
      });

      onRecordingComplete?.(true, url, url, fileName, mp3Blob);
    } catch (error) {
      console.error("Error processing audio recording:", error);
      alert("Error processing audio recording. Please try again.");
      setRecorderState("idle");
      setIsProcessing(false);
    }
  };

  const resetRecording = () => {
    if (disabled) return;

    console.log("VoiceNoteRecorder - Reset recording called");

    stopTimer();
    isResettingRef.current = true;

    // Stop recording if in progress
    if (recordPluginRef.current && recordPluginRef.current.isRecording()) {
      recordPluginRef.current.stopRecording();
    }

    // Clean up blob URLs
    if (audioUrl && audioUrl.startsWith("blob:")) {
      URL.revokeObjectURL(audioUrl);
    }

    if (
      downloadUrl &&
      downloadUrl !== audioUrl &&
      downloadUrl.startsWith("blob:")
    ) {
      URL.revokeObjectURL(downloadUrl);
    }

    // Reset all state
    setRecorderState("idle");
    setSeconds(0);
    setAudioUrl(null);
    setAudioBlob(null);
    setDownloadUrl(null);
    setFilename("");
    setProgress(0);
    isInitialized.current = false;

    console.log("VoiceNoteRecorder - Notifying parent: no recording");
    onRecordingComplete?.(false, undefined, undefined, undefined, undefined);

    // Move waveform back to hidden container and re-initialize
    setTimeout(() => {
      const displayContainer = document.getElementById(
        "recording-waveform-display",
      );
      const hiddenContainer = waveformRef.current;

      if (displayContainer && hiddenContainer) {
        // Move waveform back to hidden container
        while (displayContainer.firstChild) {
          hiddenContainer.appendChild(displayContainer.firstChild);
        }
        console.log(
          "Waveform moved back to hidden container, children count:",
          hiddenContainer.children.length,
        );
      }

      // Re-initialize WaveSurfer after reset
      if (wavesurferRef.current) {
        console.log("Destroying WaveSurfer before re-init");
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
        recordPluginRef.current = null;
      }

      if (hiddenContainer) {
        console.log("Re-initializing WaveSurfer after reset");
        try {
          const wavesurfer = WaveSurfer.create({
            container: hiddenContainer,
            waveColor: "#9ca3af",
            progressColor: "#253057",
            height: 48,
            barWidth: 2,
            barGap: 4,
            barRadius: 2,
            normalize: false,
          });

          wavesurferRef.current = wavesurfer;
          const record = wavesurfer.registerPlugin(
            RecordPlugin.create({
              renderRecordedAudio: false,
              scrollingWaveform: true,
              continuousWaveform: false,
              audioBitsPerSecond: 128000,
              mimeType: "audio/webm",
            }),
          );

          recordPluginRef.current = record;

          record.on("record-end", (blob: Blob) => {
            console.log(
              "Recording ended via WaveSurfer, blob size:",
              blob.size,
            );
            handleRecordingComplete(blob);
          });

          record.on("record-progress", (time: number) => {
            const seconds = Math.floor(time / 1000);
            setSeconds(seconds);
          });

          console.log("WaveSurfer re-initialized successfully after reset");
        } catch (error) {
          console.error("Error re-initializing WaveSurfer:", error);
        }
      }

      isResettingRef.current = false;
    }, 100);
  };

  const togglePlay = () => {
    if (disabled || !audioRef.current || !audioUrl || isProcessing) {
      console.warn(
        "VoiceNoteRecorder - togglePlay: Disabled, no audio, or still processing",
      );
      return;
    }

    const audio = audioRef.current;

    console.log("VoiceNoteRecorder - togglePlay:", {
      currentState: recorderState,
      audioReadyState: audio.readyState,
      audioDuration: audio.duration,
      audioCurrentTime: audio.currentTime,
      audioPaused: audio.paused,
      audioEnded: audio.ended,
    });

    if (recorderState === "playing") {
      console.log("VoiceNoteRecorder - Pausing audio");
      audio.pause();
      setRecorderState("recorded");
      return;
    }

    // Starting playback
    console.log("VoiceNoteRecorder - Starting audio playback");

    // Only reset to beginning if at the end
    if (audio.currentTime >= audio.duration || audio.ended) {
      audio.currentTime = 0;
      setProgress(0);
      console.log("VoiceNoteRecorder - Reset currentTime to 0 (was at end)");
    }

    // Update state to playing immediately
    setRecorderState("playing");

    const playAudio = async () => {
      try {
        await audio.play();
        console.log(
          "VoiceNoteRecorder - Audio playing successfully, currentTime:",
          audio.currentTime,
        );
      } catch (error) {
        console.error("VoiceNoteRecorder - Error playing audio:", error);
        setRecorderState("recorded");
      }
    };

    // Check if audio is ready to play
    if (audio.readyState >= 2) {
      playAudio();
    } else {
      console.log("Audio not ready, loading...");

      const handleCanPlay = () => {
        console.log("Audio ready after load");
        playAudio();
      };

      audio.addEventListener("canplay", handleCanPlay, { once: true });
      audio.load();
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || disabled || !audioRef.current || !audioUrl) return;

      const progressBar = document.getElementById("audio-progress-bar");
      if (!progressBar) return;

      const audio = audioRef.current;
      if (!audio.duration || !isFinite(audio.duration)) return;

      const rect = progressBar.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const newTime = percentage * audio.duration;

      audio.currentTime = newTime;
      setProgress(percentage);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, disabled, audioUrl]);

  // Enhanced audio event listeners with smooth progress animation
  useEffect(() => {
    if (!audioRef.current || !audioUrl) return;
    const audio = audioRef.current;

    console.log("VoiceNoteRecorder - Setting up audio event listeners", {
      audioUrl: audioUrl.substring(0, 50),
      audioUrlType: audioUrl.startsWith("blob:")
        ? "blob"
        : audioUrl.startsWith("http")
          ? "remote-api"
          : "unknown",
    });

    let animationFrameId: number | null = null;

    // Smooth progress update using requestAnimationFrame
    const updateProgress = () => {
      if (audio.duration > 0 && isFinite(audio.duration) && !audio.paused) {
        const newProgress = audio.currentTime / audio.duration;
        setProgress(Math.min(newProgress, 1));
        animationFrameId = requestAnimationFrame(updateProgress);
      }
    };

    const handlePlay = () => {
      console.log(
        "VoiceNoteRecorder - Audio playing, starting smooth progress",
      );
      updateProgress();
    };

    const handlePause = () => {
      console.log("VoiceNoteRecorder - Audio paused, stopping smooth progress");
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    };

    const handleTimeUpdate = () => {
      // Fallback update for when not playing
      if (audio.paused && audio.duration > 0 && isFinite(audio.duration)) {
        const newProgress = audio.currentTime / audio.duration;
        setProgress(Math.min(newProgress, 1));
      }
    };

    const handleEnded = () => {
      console.log("VoiceNoteRecorder - Audio ended");
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      setRecorderState("recorded");
      setProgress(1);
    };

    const handleLoadedMetadata = () => {
      console.log(
        "VoiceNoteRecorder - Audio metadata loaded, duration:",
        audio.duration,
      );
      if (audio.currentTime > 0) {
        audio.currentTime = 0;
      }
      setProgress(0);
    };

    const handleError = (e: Event) => {
      console.error("VoiceNoteRecorder - Audio playback error:", e);
      console.error("Failed audio URL:", audioUrl.substring(0, 100));
      // Don't change state on error - keep as recorded
    };

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("error", handleError);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("error", handleError);
    };
  }, [audioUrl, recorderState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();

      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }

      if (audioUrl && audioUrl.startsWith("blob:")) {
        URL.revokeObjectURL(audioUrl);
      }
      if (
        downloadUrl &&
        downloadUrl !== audioUrl &&
        downloadUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [audioUrl, downloadUrl]);

  // Calculate waveform bars based on container size
  // Use a separate ref for the visible container
  const idleWaveformRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const calculateBars = () => {
      // Use the idle waveform container itself for calculations
      const container = idleWaveformRef.current;
      if (container) {
        const width = container.offsetWidth;
        const screenWidth = window.innerWidth;

        let barWidth = 2;
        let spacing = 4;

        if (screenWidth >= 2560) {
          barWidth = 3;
          spacing = 6;
        } else if (screenWidth >= 1920) {
          barWidth = 2.5;
          spacing = 5;
        } else if (screenWidth >= 1600) {
          barWidth = 2;
          spacing = 4;
        }

        const totalBarWidth = barWidth + spacing;
        const count = Math.floor(width / totalBarWidth);
        const calculatedBars = Math.max(count, 15);
        setWaveformBars(calculatedBars);

        console.log(
          "Calculated waveform bars:",
          calculatedBars,
          "container width:",
          width,
        );
      }
    };

    // Calculate immediately and after a short delay to ensure DOM is ready
    const timer = setTimeout(calculateBars, 100);

    const resizeObserver = new ResizeObserver(() => {
      calculateBars();
    });

    if (idleWaveformRef.current) {
      resizeObserver.observe(idleWaveformRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [recorderState]);

  const generateWaveformHeights = (count: number) => {
    const pattern = [
      0.2, 0.8, 0.4, 1.0, 0.6, 0.3, 0.9, 0.1, 0.7, 0.5, 0.2, 0.6, 0.8, 0.3, 0.9,
      0.4,
    ];
    return Array.from({ length: count }, (_, i) => {
      const patternIndex = i % pattern.length;
      return pattern[patternIndex];
    });
  };

  // Handle seeking in the progress bar
  const handleProgressBarInteraction = (
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (disabled || !audioRef.current || !audioUrl) return;

    const audio = audioRef.current;
    if (!audio.duration || !isFinite(audio.duration)) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * audio.duration;

    console.log("VoiceNoteRecorder - Seeking to:", {
      percentage,
      newTime,
      duration: audio.duration,
    });

    audio.currentTime = newTime;
    setProgress(percentage);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || !audioRef.current || !audioUrl) return;
    setIsDragging(true);
    handleProgressBarInteraction(e);
  };

  const getWaveformBarStyles = () => {
    const screenWidth = window.innerWidth;

    let barWidth = "w-[2px]";
    let spacing = "gap-1";
    let baseHeight = 4;
    let maxHeight = 16;

    if (screenWidth >= 2560) {
      barWidth = "w-[3px]";
      spacing = "gap-1.5";
      baseHeight = 6;
      maxHeight = 20;
    } else if (screenWidth >= 1920) {
      barWidth = "w-[2px]";
      spacing = "gap-1.5";
      baseHeight = 5;
      maxHeight = 18;
    } else if (screenWidth >= 1600) {
      barWidth = "w-[2px]";
      spacing = "gap-1";
      baseHeight = 4;
      maxHeight = 17;
    } else if (screenWidth >= 1440) {
      barWidth = "w-[2px]";
      spacing = "gap-1";
      baseHeight = 4;
      maxHeight = 16;
    }

    return { barWidth, spacing, baseHeight, maxHeight };
  };

  const waveformStyles = getWaveformBarStyles();
  const waveformHeights = generateWaveformHeights(waveformBars);

  const ResetIcon =
    recorderState === "recording"
      ? ResetRecordingActive
      : ResetRecordingInactive;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0
      ? `${mins}:${secs.toString().padStart(2, "0")}`
      : `${secs}s`;
  };

  const disabledClasses = disabled ? "opacity-50 pointer-events-none" : "";
  const buttonDisabledClasses = disabled
    ? "cursor-not-allowed opacity-50"
    : "hover:opacity-80 transition-opacity";

  return (
    <div className={`w-full ${disabledClasses}`}>
      {!compact && (
        <div className="flex items-center justify-between mb-2">
          <span
            className={`${responsiveClasses.textSizeTitle} font-medium text-gray-700`}
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            Add voice note
          </span>
          <div className="flex items-center gap-2 mr-3">
            <span
              className={`${responsiveClasses.textSizeTime} text-gray-500`}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {formatTime(seconds)}
            </span>
          </div>
        </div>
      )}

      <div className={`flex items-center ${compact ? "gap-2" : responsiveClasses.buttonGap}`}>
        {/* Hidden waveform container - always in DOM for WaveSurfer initialization */}
        <div
          style={{
            position: "absolute",
            width: 0,
            height: 0,
            overflow: "hidden",
          }}
        >
          <div ref={waveformRef} style={{ width: "300px", height: "48px" }} />
        </div>

        <div
          className={`flex-1 ${compact ? "h-8" : responsiveClasses.containerHeight} bg-[#F4F5F6] rounded-[8px] flex items-center ${compact ? "pl-2 pr-1" : responsiveClasses.waveformPadding} overflow-hidden relative`}
        >
          {recorderState === "idle" && (
            <>
              <button
                onClick={startRecording}
                className={`text-blue-600 flex items-center justify-center mr-2 transition-colors z-10 flex-shrink-0 ${
                  disabled
                    ? "cursor-not-allowed opacity-50"
                    : "hover:text-blue-700"
                }`}
                aria-label="Start recording"
                disabled={disabled}
              >
                <MicIcon className={compact ? "w-4 h-4" : responsiveClasses.iconSize} />
              </button>
              <div
                ref={idleWaveformRef}
                className={`flex ${waveformStyles.spacing} items-center flex-1 overflow-hidden`}
              >
                {waveformHeights.map((heightRatio, i) => {
                  const height =
                    waveformStyles.baseHeight +
                    (waveformStyles.maxHeight - waveformStyles.baseHeight) *
                      heightRatio;

                  return (
                    <div
                      key={`idle-${i}`}
                      className={`${waveformStyles.barWidth} bg-gray-400 rounded-full flex-shrink-0`}
                      style={{ height: `${height}px` }}
                    />
                  );
                })}
              </div>
            </>
          )}

          {recorderState === "recording" && (
            <>
              <button
                onClick={stopRecording}
                className={`flex items-center justify-center mr-2 transition-opacity z-10 ${buttonDisabledClasses}`}
                aria-label="Stop recording"
                disabled={disabled}
              >
                <StopRecording
                  className={`${compact ? "w-4 h-4" : responsiveClasses.iconSize} text-white`}
                />
              </button>
              {compact && (
                <span
                  className="text-xs text-gray-500 mr-2 flex-shrink-0"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {formatTime(seconds)}
                </span>
              )}
              <div
                className="flex-1 h-12"
                id="recording-waveform-display"
                style={{ transform: "scaleX(-1)" }}
              />
            </>
          )}

          {(recorderState === "recorded" || recorderState === "playing") && (
            <>
              <button
                onClick={togglePlay}
                className={`text-indigo-600 flex items-center justify-center mr-2 transition-colors z-10 ${
                  disabled || isProcessing
                    ? "cursor-not-allowed opacity-50"
                    : "hover:text-indigo-700"
                }`}
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                aria-label={
                  isProcessing
                    ? "Processing audio..."
                    : recorderState === "playing"
                      ? "Pause recording"
                      : "Play recording"
                }
                disabled={isProcessing} // ✅ Disable play button while processing
              >
                {isProcessing ? (
                  // Show loading spinner while processing
                  <svg
                    className={`${compact ? "w-4 h-4" : responsiveClasses.iconSize} animate-spin`}
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : recorderState === "playing" ? (
                  <PauseRecording className={compact ? "w-4 h-4" : responsiveClasses.iconSize} />
                ) : (
                  <PlayRecording className={compact ? "w-4 h-4" : responsiveClasses.iconSize} />
                )}
              </button>
              <div className="flex-1 relative flex items-center">
                {/* Progress bar - show indeterminate if processing */}
                <div
                  id="audio-progress-bar"
                  className={`flex-1 h-1 bg-gray-300 rounded-full relative ${
                    isProcessing ? "cursor-wait" : "cursor-pointer"
                  }`}
                  onMouseDown={isProcessing ? undefined : handleMouseDown}
                  onClick={
                    isProcessing ? undefined : handleProgressBarInteraction
                  }
                >
                  {isProcessing ? (
                    // Indeterminate progress bar
                    <div
                      className="h-1 bg-[#253057] rounded-full animate-pulse"
                      style={{ width: "50%" }}
                    />
                  ) : (
                    <>
                      <div
                        className="h-1 bg-[#253057] rounded-full pointer-events-none"
                        style={{
                          width: `${progress * 100}%`,
                          transition: "none",
                        }}
                      />
                      <div
                        className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-[#49AFFF] border-4 border-[#D3E2EB] rounded-full cursor-pointer hover:scale-110 transition-transform"
                        style={{
                          left: `${progress * 100}%`,
                          marginLeft: "-8px",
                          transition: isDragging ? "none" : "transform 0.2s",
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {(recorderState === "idle" || recorderState === "recording") && (
          <button
            onClick={resetRecording}
            className={`flex items-center justify-center transition-opacity ${buttonDisabledClasses}`}
            aria-label="Reset recording"
            disabled={disabled}
          >
            <ResetIcon
              className={`${compact ? "w-6 h-6" : responsiveClasses.buttonSize} text-gray-700`}
            />
          </button>
        )}

        {(recorderState === "recorded" || recorderState === "playing") && (
          <button
            onClick={resetRecording}
            className={`flex items-center justify-center transition-opacity ${buttonDisabledClasses}`}
            aria-label="Delete recording"
            disabled={disabled}
          >
            <DeleteRecording
              className={`${compact ? "w-6 h-6" : responsiveClasses.buttonSize} text-red-600`}
            />
          </button>
        )}
      </div>

      <audio ref={audioRef} className="hidden" preload="auto" />
    </div>
  );
};

export default VoiceNoteRecorder;
